import { useState, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Alert, CircularProgress } from '@mui/material'
import { Button, Icon, Notice, Page, PageHeader } from '@/components/ui'
import { useDatabase } from '@/hooks/useDatabase'
import { DatabaseTable } from '@/components/Database/DatabaseTable'
import { ExportDialog } from '@/components/Database/ExportDialog'
import { ImportDialog } from '@/components/Database/ImportDialog'
import { ColumnManager } from '@/components/Database/ColumnManager'
import { DuplicateWarning } from '@/components/InvoiceAnalyser/DuplicateWarning'
import { useT } from '@/i18n/useT'
import type { DatabaseInvoice, ColumnDefinition, InvoiceStatus } from '@/types/database'

export default function DatabasePage() {
  const [searchParams] = useSearchParams()
  const { t } = useT()
  const {
    getInvoices,
    deleteInvoice,
    updateInvoice,
    bulkUpdateStatus,
    createInvoice,
    getColumns,
    createColumn,
    deleteColumn,
    exportInvoices,
    importFromExcel,
    checkDuplicate,
  } = useDatabase()

  const [invoices, setInvoices] = useState<DatabaseInvoice[]>([])
  const [columns, setColumns] = useState<ColumnDefinition[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  // Dialog states
  const [exportOpen, setExportOpen] = useState(false)
  const [importOpen, setImportOpen] = useState(false)
  const [columnManagerOpen, setColumnManagerOpen] = useState(false)
  const [duplicateOpen, setDuplicateOpen] = useState(false)
  const [pendingInvoice, setPendingInvoice] = useState<Partial<DatabaseInvoice> | null>(null)
  const [duplicateInfo, setDuplicateInfo] = useState<{
    supplierName: string
    rechnungsnummer: string
    dateInvoice: string | null
    duplicateId: string | null
  } | null>(null)

  const highlightId = searchParams.get('invoice')

  // Load all invoices once; filtering/sorting/pagination happen client-side in the grid
  const loadData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const invoicesData = await getInvoices({ limit: 10000 })
      setInvoices(invoicesData.data)
    } catch (err) {
      console.error('Failed to load data:', err)
      setError(err instanceof Error ? err.message : t('Could not load the data'))
    } finally {
      setLoading(false)
    }
  }, [getInvoices, t])

  // Load columns
  const loadColumns = useCallback(async () => {
    try {
      const data = await getColumns()
      setColumns(data)
    } catch (err) {
      console.error('Failed to load columns:', err)
      setError(err instanceof Error ? err.message : t('Could not load the columns'))
    }
  }, [getColumns, t])

  useEffect(() => {
    void loadData()
    void loadColumns()
  }, [loadData, loadColumns])

  // Handle invoice update
  const handleUpdateInvoice = useCallback(
    async (id: string, data: Partial<DatabaseInvoice>) => {
      try {
        await updateInvoice(id, data)
        await loadData()
        setSuccessMessage(t('Invoice updated'))
        setTimeout(() => setSuccessMessage(null), 3000)
      } catch (err) {
        setError(err instanceof Error ? err.message : t('Could not update the invoice'))
      }
    },
    [updateInvoice, loadData, t]
  )

  // Handle bulk status change (e.g. approve several invoices for payment at once)
  const handleBulkStatus = useCallback(
    async (
      ids: string[],
      status: InvoiceStatus,
      approver?: string | null,
      approvalDate?: string | null,
    ) => {
      try {
        const { updated } = await bulkUpdateStatus(ids, status, approver, approvalDate)
        await loadData()
        setSuccessMessage(
          updated === 1
            ? t('1 invoice updated')
            : t('{n} invoices updated').replace('{n}', String(updated)),
        )
        setTimeout(() => setSuccessMessage(null), 3000)
      } catch (err) {
        setError(err instanceof Error ? err.message : t('Bulk update failed'))
      }
    },
    [bulkUpdateStatus, loadData, t]
  )

  // Handle invoice delete
  const handleDeleteInvoice = useCallback(
    async (id: string) => {
      try {
        await deleteInvoice(id)
        await loadData()
        setSuccessMessage(t('Invoice deleted'))
        setTimeout(() => setSuccessMessage(null), 3000)
      } catch (err) {
        setError(err instanceof Error ? err.message : t('Could not delete the invoice'))
      }
    },
    [deleteInvoice, loadData, t]
  )

  // Separate function for actual invoice creation
  const actuallyCreateInvoice = useCallback(
    async (data: Partial<DatabaseInvoice>) => {
      try {
        await createInvoice({
          supplierName: data.supplierName || '',
          rechnungsnummer: data.rechnungsnummer || '',
          dateInvoice: data.dateInvoice || null,
          brutto: data.brutto ?? null,
          netto: data.netto ?? null,
          comment: data.comment || '',
          currency: data.currency || null,
          projectName: data.projectName || null,
          recipient: data.recipient || null,
          entity: data.entity ?? null,
          ustId: data.ustId || null,
          confidence: data.confidence || null,
          source: 'dashboard',
          customFields: data.customFields || {},
          poId: data.poId ?? null,
          status: data.status || 'open',
          approver: data.approver ?? null,
          approvalDate: data.approvalDate ?? null,
          dueDate: data.dueDate ?? null,
          auslagen: data.auslagen ?? null,
          originalAuslagen: data.originalAuslagen ?? null,
          servicePeriodStart: data.servicePeriodStart ?? null,
          servicePeriodEnd: data.servicePeriodEnd ?? null,
          legalCounsel: data.legalCounsel ?? null,
          documentType: data.documentType ?? 'invoice',
          originalInvoiceId: data.originalInvoiceId ?? null,
        })
        await loadData()
        setSuccessMessage(t('Invoice created'))
        setTimeout(() => setSuccessMessage(null), 3000)
      } catch (err) {
        setError(err instanceof Error ? err.message : t('Could not create the invoice'))
      }
    },
    [createInvoice, loadData, t]
  )

  // Handle create invoice
  const handleCreateInvoice = useCallback(
    async (data: Partial<DatabaseInvoice>) => {
      const supplierName = data.supplierName || ''
      const rechnungsnummer = data.rechnungsnummer || ''
      const dateInvoice = data.dateInvoice || null

      // Check for duplicates first
      try {
        const dupResult = await checkDuplicate({
          supplierName,
          rechnungsnummer,
        })

        if (dupResult.isDuplicate) {
          // Store pending invoice and show warning dialog
          setPendingInvoice(data)
          setDuplicateInfo({
            supplierName,
            rechnungsnummer,
            dateInvoice,
            duplicateId: dupResult.duplicateIds[0] || null,
          })
          setDuplicateOpen(true)
          return
        }
      } catch (error) {
        console.error('Duplicate check failed:', error)
        // Continue with creation on error
      }

      // Actually create the invoice
      await actuallyCreateInvoice(data)
    },
    [actuallyCreateInvoice, checkDuplicate]
  )

  // Handle duplicate warning confirm
  const handleDuplicateConfirm = useCallback(() => {
    setDuplicateOpen(false)
    if (pendingInvoice) {
      void actuallyCreateInvoice(pendingInvoice)
      setPendingInvoice(null)
    }
  }, [pendingInvoice, actuallyCreateInvoice])

  // Handle duplicate warning cancel
  const handleDuplicateCancel = useCallback(() => {
    setDuplicateOpen(false)
    setPendingInvoice(null)
    setDuplicateInfo(null)
  }, [])

  // Handle export
  const handleExport = useCallback(
    async (config: { format: 'xlsx' | 'csv'; separator: 'comma' | 'period'; includeCustomColumns: boolean }) => {
      try {
        await exportInvoices(config)
        setSuccessMessage(t('Export finished'))
        setTimeout(() => setSuccessMessage(null), 3000)
        setExportOpen(false)
      } catch (err) {
        setError(err instanceof Error ? err.message : t('Export failed'))
      }
    },
    [exportInvoices, t]
  )

  // Handle import
  const handleImport = useCallback(
    async (file: File) => {
      try {
        const result = await importFromExcel(file)
        await loadData()
        setSuccessMessage(t('{n} invoices imported').replace('{n}', String(result.imported)))
        setTimeout(() => setSuccessMessage(null), 3000)
        setImportOpen(false)
      } catch (err) {
        setError(err instanceof Error ? err.message : t('Import failed'))
      }
    },
    [importFromExcel, loadData, t]
  )

  const handleCreateColumn = useCallback(async (name: string, type: 'text' | 'number' | 'date') => {
    await createColumn(name, type)
    await loadColumns()
    setSuccessMessage(t('Column created'))
    setTimeout(() => setSuccessMessage(null), 3000)
  }, [createColumn, loadColumns, t])

  const handleDeleteColumn = useCallback(async (id: string) => {
    await deleteColumn(id)
    await loadColumns()
    setSuccessMessage(t('Column deleted'))
    setTimeout(() => setSuccessMessage(null), 3000)
  }, [deleteColumn, loadColumns, t])

  const supplierCount = new Set(
    invoices.map((i) => (i.supplierName || '').trim().toLowerCase()).filter(Boolean),
  ).size

  return (
    <Page>
      <PageHeader
        title={t('Database')}
        subtitle={`${invoices.length.toLocaleString('de-DE')} ${t('invoices')} · ${supplierCount} ${t('suppliers')} · ${columns.length} ${t('custom columns')}`}
        actions={
          <>
            <Button variant="ghost" onClick={() => setColumnManagerOpen(true)}>
              {t('Columns')}
            </Button>
            <Button variant="ghost" onClick={() => setImportOpen(true)}>
              {t('Import')}
            </Button>
            <Button variant="outline" onClick={() => setExportOpen(true)}>
              <Icon name="download" size={15} />
              {t('Export')}
            </Button>
            <Button variant="outline" onClick={() => void loadData()} disabled={loading}>
              <Icon name="refresh" size={15} strokeWidth={2.2} />
              {t('Refresh')}
            </Button>
          </>
        }
      />

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && <Notice onClose={() => setSuccessMessage(null)}>{successMessage}</Notice>}

      {/* Inline, above the table — the duplicate has to be weighed against what
          is on screen, so it is not a modal any more. */}
      {duplicateInfo && (
        <DuplicateWarning
          open={duplicateOpen}
          supplierName={duplicateInfo.supplierName}
          rechnungsnummer={duplicateInfo.rechnungsnummer}
          dateInvoice={duplicateInfo.dateInvoice}
          duplicateId={duplicateInfo.duplicateId}
          onConfirm={handleDuplicateConfirm}
          onCancel={handleDuplicateCancel}
        />
      )}

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 64 }}>
          <CircularProgress />
        </div>
      ) : (
        <DatabaseTable
          invoices={invoices}
          columns={columns}
          highlightId={highlightId}
          onEdit={handleUpdateInvoice}
          onDelete={handleDeleteInvoice}
          onCreate={handleCreateInvoice}
          onBulkStatus={handleBulkStatus}
        />
      )}

      {/* Dialogs */}
      <ExportDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        onExport={handleExport}
      />

      <ImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImport={handleImport}
      />

      <ColumnManager
        open={columnManagerOpen}
        onClose={() => setColumnManagerOpen(false)}
        columns={columns}
        onCreateColumn={handleCreateColumn}
        onDeleteColumn={handleDeleteColumn}
      />
    </Page>
  )
}
